import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Users, MoreVertical, Ban, CheckCircle, Trash2, Server, RefreshCw, KeyRound } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminUsers = () => {
  const { getAuthHeader } = useAuth();
  const [users, setUsers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [userToReset, setUserToReset] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [tempPassword, setTempPassword] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, plansRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/users`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/plans`)
      ]);
      setUsers(usersRes.data);
      setPlans(plansRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/suspend`, {}, { headers: getAuthHeader() });
      toast.success("Utilisateur suspendu");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suspension");
    }
  };

  const handleActivate = async (userId) => {
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/activate`, {}, { headers: getAuthHeader() });
      toast.success("Utilisateur active");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'activation");
    }
  };

  const handleDelete = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userToDelete.id}`, { headers: getAuthHeader() });
      toast.success("Utilisateur supprime");
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleResetPassword = async () => {
    if (!userToReset) return;
    try {
      const res = await axios.put(
        `${API_URL}/api/admin/users/${userToReset.id}/reset-password`,
        { new_password: newPassword || "" },
        { headers: getAuthHeader() }
      );
      setTempPassword(res.data.temporary_password);
      toast.success("Mot de passe reinitialise");
      setNewPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la reinitialisation");
    }
  };

  const handleChangePlan = async (userId, planId) => {
    try {
      await axios.put(
        `${API_URL}/api/admin/users/${userId}/subscription?plan_id=${planId}&status=active`,
        {},
        { headers: getAuthHeader() }
      );
      toast.success("Abonnement mis a jour");
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la mise a jour");
    }
  };

  const getStatusBadge = (subscription) => {
    if (!subscription) return <span className="px-2 py-1 rounded text-xs bg-slate-500/20 text-slate-400">Aucun</span>;
    
    const colors = {
      active: "bg-emerald-500/20 text-emerald-400",
      trial: "bg-amber-500/20 text-amber-400",
      suspended: "bg-red-500/20 text-red-400",
      cancelled: "bg-slate-500/20 text-slate-400",
      expired: "bg-red-500/20 text-red-400"
    };
    
    return (
      <span className={`px-2 py-1 rounded text-xs ${colors[subscription.status] || colors.cancelled}`}>
        {subscription.status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white">Utilisateurs</h1>
          <p className="text-slate-400 mt-1">{users.length} utilisateurs inscrits</p>
        </div>
        <Button onClick={fetchData} variant="secondary" className="bg-slate-800 hover:bg-slate-700">
          <RefreshCw className="mr-2 h-4 w-4" />
          Actualiser
        </Button>
      </div>

      {/* Delete dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Supprimer l&apos;utilisateur</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Etes-vous sur de vouloir supprimer {userToDelete?.email} ? Toutes ses donnees seront perdues.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-white hover:bg-slate-700">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset password dialog */}
      <AlertDialog open={resetDialogOpen} onOpenChange={(open) => { setResetDialogOpen(open); if (!open) { setTempPassword(null); setNewPassword(""); } }}>
        <AlertDialogContent className="bg-slate-900 border-slate-700">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Reinitialiser le mot de passe</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400" asChild>
              <div>
                {tempPassword ? (
                  <div className="space-y-3 mt-2">
                    <p className="text-emerald-400 font-medium">Mot de passe reinitialise avec succes !</p>
                    <div className="bg-slate-950 p-3 rounded-lg border border-slate-700">
                      <p className="text-xs text-slate-500 mb-1">Nouveau mot de passe :</p>
                      <p className="text-white font-mono text-lg select-all" data-testid="temp-password-display">{tempPassword}</p>
                    </div>
                    <p className="text-xs text-amber-400">Copiez ce mot de passe et transmettez-le a l&apos;utilisateur de maniere securisee.</p>
                  </div>
                ) : (
                  <div className="space-y-3 mt-2">
                    <p>Reinitialiser le mot de passe de <strong className="text-white">{userToReset?.email}</strong></p>
                    <input
                      type="text"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Laisser vide pour generer automatiquement"
                      className="w-full bg-slate-950 border border-slate-700 text-white placeholder:text-slate-500 rounded-md px-3 py-2 text-sm focus:border-indigo-500 outline-none"
                      data-testid="admin-reset-password-input"
                    />
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-slate-800 text-white hover:bg-slate-700">
              {tempPassword ? "Fermer" : "Annuler"}
            </AlertDialogCancel>
            {!tempPassword && (
              <Button onClick={handleResetPassword} className="bg-indigo-600 hover:bg-indigo-700">
                Reinitialiser
              </Button>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Users table */}
      <div className="glass-card rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-slate-900/50">
            <tr>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Utilisateur</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Entreprise</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Statut</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Formule</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Usage</th>
              <th className="text-left p-4 text-sm font-medium text-slate-400">Inscription</th>
              <th className="text-right p-4 text-sm font-medium text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id} className="border-t border-slate-800 hover:bg-slate-800/30">
                <td className="p-4">
                  <div>
                    <p className="text-white">{user.email}</p>
                    <p className="text-xs text-slate-500">{user.role}</p>
                  </div>
                </td>
                <td className="p-4 text-slate-300">{user.company_name}</td>
                <td className="p-4">{getStatusBadge(user.subscription)}</td>
                <td className="p-4">
                  <Select
                    value={user.subscription?.plan_id || ""}
                    onValueChange={(value) => handleChangePlan(user.id, value)}
                  >
                    <SelectTrigger className="w-36 bg-slate-950 border-slate-700 text-white text-sm">
                      <SelectValue placeholder="Choisir" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {plans.map((plan) => (
                        <SelectItem key={plan.id} value={plan.id}>
                          {plan.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4">
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Server size={14} />
                    <span>{user.stats?.servers || 0} serveurs</span>
                  </div>
                </td>
                <td className="p-4 text-sm text-slate-400">
                  {new Date(user.created_at).toLocaleDateString("fr-FR")}
                </td>
                <td className="p-4 text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                        <MoreVertical size={18} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-slate-900 border-slate-700">
                      {user.subscription?.status === "suspended" ? (
                        <DropdownMenuItem
                          onClick={() => handleActivate(user.id)}
                          className="text-emerald-400 hover:text-emerald-300"
                        >
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Activer
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem
                          onClick={() => handleSuspend(user.id)}
                          className="text-amber-400 hover:text-amber-300"
                        >
                          <Ban className="mr-2 h-4 w-4" />
                          Suspendre
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={() => {
                          setUserToReset(user);
                          setTempPassword(null);
                          setNewPassword("");
                          setResetDialogOpen(true);
                        }}
                        className="text-indigo-400 hover:text-indigo-300"
                        data-testid={`reset-password-${user.id}`}
                      >
                        <KeyRound className="mr-2 h-4 w-4" />
                        Reinitialiser MDP
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => {
                          setUserToDelete(user);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminUsers;
